interface ErrorDetail {
  field: string;
  message: string;
}

interface StandardizedErrorData {
  error: {
    message: string;
    details?: ErrorDetail[];
  };
}

interface LegacyErrorData {
  message: string;
}

interface ApiErrorResponse {
  data: StandardizedErrorData | LegacyErrorData | string | unknown;
  status: number;
}

interface ApiError extends Error {
  response?: ApiErrorResponse;
}

interface SuccessResponse {
  message?: string;
}

function isStandardizedErrorData(data: unknown): data is StandardizedErrorData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as StandardizedErrorData).error === 'object' &&
    (data as StandardizedErrorData).error !== null &&
    'message' in (data as StandardizedErrorData).error
  );
}

function isLegacyErrorData(data: unknown): data is LegacyErrorData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    typeof (data as LegacyErrorData).message === 'string'
  );
}

export const extractErrorMessage = (error: unknown): string => {
  const apiError = error as ApiError;
  if (!apiError.response) {
    return apiError.message || 'Network error. Please check your connection.';
  }

  const { data, status } = apiError.response;

  if (isStandardizedErrorData(data)) {
    const { message, details } = data.error;
    
    // If there are field-specific validation errors, format them nicely
    if (details && Array.isArray(details) && details.length > 0) {
      const fieldErrors = details
        .filter((detail): detail is ErrorDetail => 
          typeof detail === 'object' &&
          detail !== null &&
          'field' in detail &&
          'message' in detail &&
          typeof detail.field === 'string' &&
          typeof detail.message === 'string'
        )
        .map(detail => `${detail.field}: ${detail.message}`)
        .join('\n');
      
      if (fieldErrors) {
        return fieldErrors;
      }
    }
    
    return message;
  }

  // Handle legacy format (fallback)
  if (isLegacyErrorData(data)) {
    return data.message;
  }

  // Handle specific status codes with context-aware messages
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Incorrect email or password.';
    case 403:
      // Try to provide more specific messaging for common 403 scenarios
      if (typeof data === 'string' && data.toLowerCase().includes('google')) {
        return 'This account was created using Google. Please sign in with Google.';
      }
      return 'Access denied. You may need to use a different sign-in method.';
    case 404:
      // Context-aware 404 messages
      if (typeof data === 'string' && data.toLowerCase().includes('email')) {
        return 'Email address not found in our system.';
      }
      return 'The requested resource was not found.';
    case 409:
      return 'This information already exists in our system.';
    case 422:
      return 'Please check your input and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Server error. Please try again later.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Extract success message from API response
 * @param response - The response object from API call
 * @returns Success message
 */
export const extractSuccessMessage = (response: SuccessResponse | unknown): string => {
  if (
    typeof response === 'object' &&
    response !== null &&
    'message' in response
  ) {
    const message = (response as SuccessResponse).message;
    if (typeof message === 'string') {
      return message;
    }
  }
  return 'Operation completed successfully.';
}; 